import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaPalette, FaArrowRight, FaClock, FaUser, FaUsers, FaSpinner, FaCheckCircle } from "react-icons/fa";
import SEO from "../../components/common/SEO";
import PageSection from "../../components/common/PageSection";
import Breadcrumb from "../../components/common/Breadcrumb";
import { useAuth } from "../../context/AuthContext";
import { fetchCourses, fetchMyEnrollments } from "../../services/courseService";
import { useEnrollCourse } from "../../hooks/useEnrollCourse";
import { STATIC_COURSES, getCoursePath, withoutStaticDuplicates } from "../../config/constants/courseData";

import TeacherEnrollNote from '../../components/common/TeacherEnrollNote';
const Courses = () => {
  const { isStudent, isTeacher, getAuthHeaders } = useAuth();
  const { enroll, enrollingId } = useEnrollCourse();
  const [dynamicCourses, setDynamicCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [enrolledIds, setEnrolledIds] = useState(new Set());

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        const calls = [fetchCourses()];
        if (isStudent) calls.push(fetchMyEnrollments(getAuthHeaders()));

        const [coursesResult, enrollmentsResult] = await Promise.allSettled(calls);

        if (coursesResult.status === 'fulfilled') {
          setDynamicCourses(withoutStaticDuplicates(coursesResult.value));
        }
        if (enrollmentsResult && enrollmentsResult.status === 'fulfilled') {
          setEnrolledIds(new Set(enrollmentsResult.value.map((e) => e.course_id)));
        }
      } catch (err) {
        console.error('Failed to load courses:', err);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [isStudent, getAuthHeaders]);

  const handleEnroll = (courseId, courseTitle) => {
    enroll(courseId, courseTitle, (id) => {
      setEnrolledIds((prev) => new Set([...prev, id]));
    });
  };

  return (
    <>
      <SEO
        title="Courses - Graphic Design Training | Arvix Creative"
        description="Professional graphic design courses and training programs. Learn essential design skills from industry experts."
        keywords="graphic design course, design training, creative courses, design education, arvix creative courses"
      />
      <PageSection className="pt-24 sm:pt-28 md:pt-32 pb-12 md:pb-16">
        <div className="w-full">
          <Breadcrumb items={[{ label: "Courses" }]} className="mb-8" />
          {/* Header */}
          <div className="text-center mb-12 animate-slide-down">
            <p className="text-xs tracking-[0.25em] text-designer-primary mb-3 font-semibold">OUR COURSES</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-designer-dark text-balance mb-4">
              Learn From Industry
              <span className="text-designer-primary"> Experts</span>
            </h1>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive training programs designed to help you master modern design skills and build a successful career.
            </p>
          </div>

          {/* Static Courses */}
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {STATIC_COURSES.map((course, index) => (
                <div
                  key={course.id}
                  className="animate-fade-in"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="relative h-full bg-white/80 backdrop-blur-sm rounded-2xl border border-red-100/50 shadow-red shadow-red-hover transition-all duration-300 hover:-translate-y-2 group overflow-hidden">
                    <div className="p-6 md:p-8">
                      <div className="mb-6">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-red-50 to-red-100 text-designer-primary group-hover:scale-110 group-hover:shadow-lg transition-all duration-300">
                          <FaPalette className="w-8 h-8" />
                        </div>
                      </div>
                      <h3 className="text-2xl md:text-3xl font-bold text-designer-dark mb-4 group-hover:text-designer-primary transition-colors duration-300">
                        {course.title}
                      </h3>
                      <p className="text-gray-600 mb-6 leading-relaxed">{course.description}</p>
                      <div className="flex flex-wrap gap-4 mb-6">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <FaClock className="text-designer-primary" />
                          <span>{course.duration}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <FaUser className="text-designer-primary" />
                          <span>{course.level}</span>
                        </div>
                      </div>
                      <div className="mb-6">
                        <h4 className="text-sm font-semibold text-designer-dark mb-3">What You'll Learn:</h4>
                        <ul className="space-y-2">
                          {course.topics.slice(0, 3).map((topic, topicIndex) => (
                            <li key={topicIndex} className="flex items-start gap-2 text-sm text-gray-600">
                              <span className="text-designer-primary mt-1.5">•</span>
                              <span>{topic}</span>
                            </li>
                          ))}
                          {course.topics.length > 3 && (
                            <li className="text-sm text-designer-primary font-medium">
                              +{course.topics.length - 3} more topics
                            </li>
                          )}
                        </ul>
                      </div>
                      <Link
                        to={getCoursePath(course)}
                        className="inline-flex items-center justify-center gap-2 w-full px-6 py-3 bg-gradient-to-r from-designer-primary to-red-600 text-white font-semibold rounded-xl hover:shadow-xl hover:shadow-red-500/30 hover:scale-105 transition-all duration-300 group"
                      >
                        View Details
                        <FaArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}

              {/* Dynamic courses from teachers */}
              {isLoading ? (
                <div className="flex items-center justify-center col-span-full py-8">
                  <FaSpinner className="animate-spin text-designer-primary w-7 h-7" />
                </div>
              ) : (
                dynamicCourses.map((course, index) => (
                  <div
                    key={`dynamic-${course.id}`}
                    className="animate-fade-in"
                    style={{ animationDelay: `${(STATIC_COURSES.length + index) * 0.1}s` }}
                  >
                    <div className="relative h-full bg-white/80 backdrop-blur-sm rounded-2xl border border-red-100/50 shadow-red shadow-red-hover transition-all duration-300 hover:-translate-y-2 group overflow-hidden flex flex-col">
                      <div className="p-6 md:p-8 flex flex-col flex-1">
                        {/* Icon */}
                        <div className="mb-6">
                          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-red-50 to-red-100 text-designer-primary group-hover:scale-110 group-hover:shadow-lg transition-all duration-300">
                            <FaPalette className="w-8 h-8" />
                          </div>
                        </div>

                        {/* Title */}
                        <h3 className="text-xl md:text-2xl font-bold text-designer-dark mb-3 group-hover:text-designer-primary transition-colors duration-300">
                          {course.title}
                        </h3>

                        {/* Description */}
                        <p className="text-gray-600 mb-5 leading-relaxed text-sm flex-1">{course.description}</p>

                        {/* Meta */}
                        <div className="flex flex-wrap gap-3 mb-5">
                          {course.duration && (
                            <div className="flex items-center gap-1.5 text-sm text-gray-600">
                              <FaClock className="text-designer-primary w-3.5 h-3.5" />
                              <span>{course.duration}</span>
                            </div>
                          )}
                          {course.level && (
                            <div className="flex items-center gap-1.5 text-sm text-gray-600">
                              <FaUser className="text-designer-primary w-3.5 h-3.5" />
                              <span>{course.level}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-1.5 text-sm text-gray-500">
                            <FaUsers className="text-designer-primary w-3.5 h-3.5" />
                            <span>{course.enrollment_count || 0} enrolled</span>
                          </div>
                        </div>

                        {/* Teacher */}
                        <p className="text-xs text-gray-400 mb-5">
                          By <span className="font-medium text-gray-600">{course.teacher_name}</span>
                        </p>

                        {/* Enroll / View Details */}
                        {enrolledIds.has(course.id) ? (
                          <div className="flex items-center justify-center gap-2 w-full px-6 py-3 bg-green-50 text-green-600 font-semibold rounded-xl border border-green-200 text-sm">
                            <FaCheckCircle className="w-4 h-4" />
                            Enrolled
                          </div>
                        ) : (
                          <div className="flex flex-col gap-3">
                            <Link
                              to={getCoursePath(course)}
                              className="flex items-center justify-center gap-2 w-full px-6 py-3 bg-white text-designer-dark font-semibold rounded-xl border border-red-100 hover:border-designer-primary hover:text-designer-primary transition-all text-sm"
                            >
                              View Details
                              <FaArrowRight className="w-4 h-4" />
                            </Link>
                            {isTeacher ? (
                              <TeacherEnrollNote />
                            ) : (
                              <button
                                onClick={() => handleEnroll(course.id, course.title)}
                                disabled={enrollingId === course.id}
                                className="flex items-center justify-center gap-2 w-full px-6 py-3 bg-gradient-to-r from-designer-primary to-red-600 text-white font-semibold rounded-xl hover:shadow-xl hover:shadow-red-500/30 hover:scale-105 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed disabled:scale-100 text-sm"
                              >
                                {enrollingId === course.id ? (
                                  <>
                                    <FaSpinner className="animate-spin w-4 h-4" />
                                    Enrolling...
                                  </>
                                ) : (
                                  <>
                                    Enroll Now
                                    <FaArrowRight className="w-4 h-4" />
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Footer Note */}
          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-red-50 rounded-full border border-red-200/50 max-w-2xl mx-auto">
              <span className="text-sm text-gray-600">
                <span className="font-semibold text-designer-dark">More courses coming soon!</span>{" "}
                Stay tuned for updates on new training programs.
              </span>
            </div>
          </div>
        </div>
      </PageSection>
    </>
  );
};

export default Courses;
